import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { FileTextIcon, ImageIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { cn } from "../../../utils";
import { Attachment, AttachmentAction, AttachmentActions, AttachmentContent, AttachmentDescription, AttachmentGroup, AttachmentMedia, AttachmentTitle } from "../chat/attachment";
/**
 * The glyph for a file, chosen from its MEDIA TYPE and never from a filename or
 * a key: the stored key of a re-encoded photo can carry a different extension
 * than its bytes, and only the media type travels with the part.
 */ export function ChatAttachmentIcon({ mediaType }) {
    return mediaType?.startsWith("image/") ? /*#__PURE__*/ _jsx(ImageIcon, {
        "aria-hidden": true
    }) : /*#__PURE__*/ _jsx(FileTextIcon, {
        "aria-hidden": true
    });
}
const ATTACHMENT_STATE = {
    uploading: "uploading",
    ready: "done",
    error: "error"
};
function ComposingAttachment({ draft, description, onRemove, removeLabel }) {
    return /*#__PURE__*/ _jsxs(Attachment, {
        size: "sm",
        state: ATTACHMENT_STATE[draft.status],
        children: [
            /*#__PURE__*/ _jsx(AttachmentMedia, {
                children: draft.status === "error" ? /*#__PURE__*/ _jsx(TriangleAlertIcon, {
                    "aria-hidden": true
                }) : /*#__PURE__*/ _jsx(ChatAttachmentIcon, {
                    mediaType: draft.mediaType
                })
            }),
            /*#__PURE__*/ _jsxs(AttachmentContent, {
                children: [
                    /*#__PURE__*/ _jsx(AttachmentTitle, {
                        children: draft.filename
                    }),
                    description && /*#__PURE__*/ _jsx(AttachmentDescription, {
                        children: description
                    }),
                    draft.status === "uploading" && /*#__PURE__*/ _jsx("div", {
                        role: "progressbar",
                        "aria-label": draft.filename,
                        "aria-valuenow": draft.progress,
                        "aria-valuemin": 0,
                        "aria-valuemax": 100,
                        className: "mt-1 h-1 w-full overflow-hidden rounded-full bg-muted",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "h-full bg-primary",
                            style: {
                                width: `${draft.progress}%`
                            }
                        })
                    })
                ]
            }),
            onRemove && /*#__PURE__*/ _jsx(AttachmentActions, {
                children: /*#__PURE__*/ _jsx(AttachmentAction, {
                    "aria-label": removeLabel,
                    onClick: ()=>onRemove(draft.id),
                    children: /*#__PURE__*/ _jsx(XIcon, {
                        "aria-hidden": true
                    })
                })
            })
        ]
    });
}
/**
 * The chip row for the turn being composed, drawn above whatever composer the
 * host renders — `AiChatPanel`'s own, or a surface that builds its input row
 * itself (Bliksy). The chips carry the upload's progress and its refusal, so a
 * second surface must not redraw them from the same primitives.
 *
 * The projection is memoised because the hosts re-render on every streamed
 * token while the drafts sit still: keep `describe` stable and a whole answer
 * streams without rebuilding one chip.
 */ export function AiChatComposingAttachments({ attachments, describe, onRemove, removeLabel, flush = false, className }) {
    const chips = React.useMemo(()=>attachments.map((draft)=>/*#__PURE__*/ _jsx(ComposingAttachment, {
                draft: draft,
                description: describe(draft),
                onRemove: onRemove,
                removeLabel: removeLabel
            }, draft.id)), [
        attachments,
        describe,
        onRemove,
        removeLabel
    ]);
    if (chips.length === 0) return null;
    return /*#__PURE__*/ _jsx(AttachmentGroup, {
        className: cn(!flush && "px-4 pb-2", className),
        children: chips
    });
}

//# sourceMappingURL=ai-chat-composing-attachments.js.map